import React, {useState} from 'react';
import styled from 'styled-components';
import {FlexColumn, FlexRow, Heading, Icon, Text, TextField, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';

const tokens = tokensForThemeProvider.sky.sys;

const HeaderWrapper = styled(FlexColumn)`
	gap: ${tokens.spacing['100']};
`;

const NameRow = styled(FlexRow)`
	gap: ${tokens.spacing['200']};
`;

/* the design: breadcrumb "Reports": 16px / weight 350 / #232334. DS Text (size 1) already
 * gives 16px + the dark main colour, but its body token weight is heavier than 350; pin the weight
 * so the eyebrow matches the design's lighter breadcrumb. */
const ReportsEyebrow = styled(Text)`
	font-weight: 350;
`;

/* the design: renders the name-edit affordance as a plain dark pencil glyph (#232334),
 * not a brand-coloured low-emphasis Button (which tints the icon blue). A reset <button> keeps it
 * keyboard-accessible while letting the DS Icon own the colour via newColor="onlight" (#232334). */
const IconButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
`;

/** Page-header block sitting above the period/date/buttons controls row, per the design: a small "Reports" eyebrow, then the report name as a large
 * heading. Editors additionally see a trailing pencil icon button that toggles an inline editable
 * field bound to `useReportBuilder().setName` (the same live-update-per-keystroke behaviour the
 * name input previously had in `EditTopBar`, now relocated here). Readers (and view-mode, per the
 * `the design` render, which shows no pencil) get a read-only heading only. */
export function ReportPageHeader(): JSX.Element {
	const {formatMessage} = useIntl();
	const {definition, mode, role, setName} = useReportBuilder();
	const [isEditingName, setIsEditingName] = useState(false);

	const canEditName = role === 'editor' && mode === 'edit';

	const handleCommit = (): void => setIsEditingName(false);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
		if (event.key === 'Enter') {
			handleCommit();
		}
	};

	return (
		<HeaderWrapper data-cy={'ai-report-page-header'}>
			{/* the design: the "Reports" eyebrow/breadcrumb is the dark main content colour
			 * (#232334) at 16px, NOT a muted/auxiliary grey. `Text` already defaults to `color.main`
			 * (#232334) — the previous `auxiliary` override was the wrong (grey) token. */}
			<ReportsEyebrow>{formatMessage({id: 'ai_report_builder.reports_breadcrumb'})}</ReportsEyebrow>
			{isEditingName && canEditName ? (
				<div onKeyDown={handleKeyDown}>
					<TextField
						value={definition.name}
						onValueChange={setName}
						onBlur={handleCommit}
						autofocus
						aria-label={formatMessage({id: 'ai_report_builder.report_name'})}
						data-cy={'ai-report-name-input'}
					/>
				</div>
			) : (
				<NameRow alignItems={'center'}>
					<Heading size={'3'}>{definition.name}</Heading>
					{canEditName ? (
						<IconButton
							onClick={() => setIsEditingName(true)}
							aria-label={formatMessage({id: 'ai_report_builder.edit_report_name'})}
							data-cy={'ai-report-name-edit-button'}
						>
							<Icon icon={'edit'} newColor={'onlight'} size={'m'} />
						</IconButton>
					) : null}
				</NameRow>
			)}
		</HeaderWrapper>
	);
}
